import { Checkbox, Icon, TextInput, Tooltip } from '@doxyme/ui';
import classNames from 'classnames';
import Downshift from 'downshift';
import { useFormik } from 'formik';
import { isEmpty } from 'lodash';
import React, { createRef, useCallback, useEffect, useLayoutEffect, useRef, useState, } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { useAsyncEffect } from 'use-async-effect';
import { PasswordField } from '../../../../components/password';
import { InvisibleReCAPTCHA, } from '../../../../components/recaptcha';
import { routerHelperFactory } from '../../../../global/common/services/route-helper-factory';
import config from '../../../../global/env.config';
import { selectInstitution, selectUser, } from '../../../../store/features/system';
import { isLikelyRecaptchaFocusedElement, useCaptchaTokens, } from '../../../../utility/recaptcha-helpers';
import { trackCustomEventGA4 } from '../../../../global/global.tracking';
import { actions as coreActions, GrowthBookFeatures, hasGrowthBookFeature, useLocalizedProviderTitles, useProviderTitleLocalization, } from '../../../core';
import { ProgressButton } from '../../../core/components';
import { MAX_SLUG_LENGTH, PlanTypes, ToastType } from '../../../core/constants';
import { getPasswordStrength } from '../../../core/helpers';
import { getAngularService } from '../../../core/helpers/get-angular-service';
import { checkEmailUnique, checkRoomNameUnique, createAccountAndLogin, getUserDataByInviteToken, storePendingUser, } from '../../actions';
import * as types from '../../constants/action-types';
import { getUserCurrency, validateSignUpForm as validate } from '../../helpers';
import { getLoginError, isLoggingIn } from '../../selectors';
import './sign-up.scss';
import { SignUpStepper } from '../payment-setup/common/SignUpStepper';
import ArrowBack from '../payment-setup/common/arrow-back.svg';
import { SIGN_UP_ERROR_IDS, fieldInvalidProps, showSignUpFieldError, } from './sign-up-a11y';
import { SignUpFieldError } from './sign-up-field-error';
const { LOGIN } = types;
const VALID_NAME_LENGTH = 30;
const STORAGE_NAME_CURRENCY = 'currencyFromPricingPage';
const MAX_CHECK_IN_LINK = 15;
export const SignUp = () => {
    const dispatch = useDispatch();
    const intl = useIntl();
    // eslint-disable-next-line @typescript-eslint/unbound-method
    const { formatMessage } = intl;
    const { emitCaptchaEvent, getCaptchaToken } = useCaptchaTokens();
    const currentUser = useSelector(selectUser);
    const currentInstitution = useSelector(selectInstitution) || {};
    const isLoading = useSelector(isLoggingIn);
    const error = useSelector(getLoginError);
    const history = useHistory();
    const captchaRef = createRef();
    const backToPricingLinkRef = useRef(null);
    const emailAvailabilityCheckInFlightRef = useRef(false);
    const queryParams = useLocation();
    // 'professional' account type is not explicitly passed in. It is assumed if the accountType is not 'free' or 'clinic'.
    // see https://doxyme.atlassian.net/browse/CS-17394
    const accountType = new URLSearchParams(queryParams.search).get('accountType') ?? PlanTypes.Professional;
    const hasClinicSignUpPathFlag = hasGrowthBookFeature(GrowthBookFeatures.ClinicSignUpPath);
    const hasClinicSignUpPath = Boolean(accountType === PlanTypes.Clinic && hasClinicSignUpPathFlag);
    const showPaymentSetup = Boolean(accountType === PlanTypes.Professional || hasClinicSignUpPath);
    const inviteToken = new URLSearchParams(queryParams.search).get('token');
    const prefilledEmail = new URLSearchParams(queryParams.search).get('email') ?? '';
    const decodedPrefilledEmail = decodeURIComponent(prefilledEmail);
    const promoCode = new URLSearchParams(queryParams.search).get('promo');
    const isClinicInvite = Boolean(inviteToken);
    const marketingUrl = routerHelperFactory.getBaseUrl();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [fadeOutClass, setFadeOutClass] = useState('');
    const [hasAgreedTermsAndCond, setHasAgreedTermsAndCond] = useState(false);
    const [hasConfirmedProviderStatus, setHasConfirmedProviderStatus] = useState(isClinicInvite);
    const [hasContinueBeenClicked, setHasContinueBeenClicked] = useState(false);
    const [isEmailTaken, setIsEmailTaken] = useState(false);
    const [isRoomNameTaken, setIsRoomNameTaken] = useState(false);
    const [isTitleSelectOpen, setIsTitleSelectOpen] = useState(false);
    const [isRoomNameDisabled, setIsRoomNameDisabled] = useState(false);
    const [loadingRoom, setLoadingRoom] = useState(false);
    const [loadingEmail, setLoadingEmail] = useState(false);
    const [passwordStrengthClass, setPasswordStrengthClass] = useState('');
    const [roomName, setRoomName] = useState('');
    const [isFormComplete, setIsFormComplete] = useState(false);
    const [emailIsAvailable, setEmailIsAvailable] = useState(false);
    const [clinicDomain, setClinicDomain] = useState('');
    const [isDomainTaken, setIsDomainTaken] = useState(false);
    const [recaptchaReady, setRecaptchaReady] = useState(false);
    useEffect(() => {
        if (!recaptchaReady) {
            // eslint-disable-next-line @typescript-eslint/no-empty-function -- no-op cleanup so all branches return a disposer
            return () => { };
        }
        const tryBounceFocusFromRecaptcha = () => {
            const emailInput = document.querySelector('[data-test-id="sign-up-email"] input:not([disabled])');
            if (!emailInput) {
                return;
            }
            if (isLikelyRecaptchaFocusedElement(document.activeElement)) {
                emailInput.focus({ preventScroll: true });
            }
        };
        const t1 = window.setTimeout(tryBounceFocusFromRecaptcha, 0);
        const t2 = window.setTimeout(tryBounceFocusFromRecaptcha, 120);
        const t3 = window.setTimeout(tryBounceFocusFromRecaptcha, 500);
        return () => {
            window.clearTimeout(t1);
            window.clearTimeout(t2);
            window.clearTimeout(t3);
        };
    }, [recaptchaReady]);
    const institution = getAngularService('Institution');
    const getUserValues = useCallback((user) => {
        const values = {
            accountType,
            email: '',
            title: 'Other',
            firstName: '',
            lastName: '',
            roomName: '',
            password: '',
            ...(hasClinicSignUpPath && { clinicName: '' }),
            ...(hasClinicSignUpPath && { clinicDomain: '' }),
        };
        if (!user) {
            return values;
        }
        if (user.roomName) {
            setRoomName(user.roomName);
            if (inviteToken) {
                setIsRoomNameDisabled(true);
            }
        }
        for (const key of Object.keys(values)) {
            values[key] = user[key] || values[key];
        }
        return values;
    }, []);
    const fetchUserDataByToken = useCallback(async () => {
        const user = await dispatch(getUserDataByInviteToken(inviteToken));
        if (user) {
            setValues(getUserValues(user), true);
            setEmailIsAvailable(true);
            setIsEmailTaken(false);
        }
    }, [inviteToken]);
    useEffect(() => {
        if (inviteToken) {
            fetchUserDataByToken();
        }
    }, []);
    // Worth noting that type "Other" becomes saved to currentUser as '', thus the below logic.
    // This ensures the field displays correctly with "Go Back" from /payment-setup, as well as form submission.
    useEffect(() => {
        if (currentUser) {
            setEmailIsAvailable(true);
            if (currentUser.title !== '') {
                setFieldValue('title', currentUser.title, true);
            }
            else {
                setFieldValue('title', 'Other', true);
            }
        }
        if (!currentUser && !isEmpty(intl.messages)) {
            setFieldValue('title', 'Other', true);
        }
    }, [currentUser, intl.messages]);
    const { errors, values, touched, validateForm, setFieldValue, handleChange, setValues, handleSubmit, setFieldError, setFieldTouched, } = useFormik({
        initialValues: {
            accountType,
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            email: currentUser != null ? currentUser.email : decodedPrefilledEmail,
            title: currentUser ? currentUser.title : 'Other',
            firstName: currentUser ? currentUser.firstName : '',
            lastName: currentUser ? currentUser.lastName : '',
            roomName: currentUser ? currentUser.roomName : '',
            password: currentUser ? currentUser.password : '',
            ...(hasClinicSignUpPath && { clinicName: currentUser?.clinicName ?? '' }),
            ...(hasClinicSignUpPath && {
                clinicDomain: currentUser?.clinicDomain ?? '',
            }),
        },
        validate: (val) => validate(val, hasClinicSignUpPath),
        onSubmit: async (values) => {
            await handleSignupSubmit({
                handleCheckRoomNameAvailability,
                inviteToken,
                hasAgreedTermsAndCond,
                hasConfirmedProviderStatus,
                accountType,
                dispatch,
                emailIsAvailable,
                history,
                values,
                recaptchaToken: await getCaptchaToken(captchaRef.current?.getWidgetId()),
                hasClinicSignUpPath,
                promoCode,
            });
        },
    });
    const emailFieldDisabledForFocus = Boolean(inviteToken && values.email);
    const focusSignUpEmailInput = useCallback(() => {
        const emailInput = document.querySelector('[data-test-id="sign-up-email"] input:not([disabled])');
        if (emailInput) {
            emailInput.focus({ preventScroll: true });
            return true;
        }
        return false;
    }, []);
    const focusSignUpTitleField = useCallback(() => {
        document
            .querySelector('[data-test-id="sign-up-title"]')
            ?.focus();
    }, []);
    useLayoutEffect(() => {
        if (emailFieldDisabledForFocus) {
            focusSignUpTitleField();
            // eslint-disable-next-line @typescript-eslint/no-empty-function -- no-op cleanup so all branches return a disposer
            return () => { };
        }
        let cancelled = false;
        const tryFocusEmail = () => {
            if (!cancelled) {
                focusSignUpEmailInput();
            }
        };
        tryFocusEmail();
        const rafId = window.requestAnimationFrame(tryFocusEmail);
        const timeoutIds = [0, 50, 100, 200, 400].map((delay) => window.setTimeout(tryFocusEmail, delay));
        return () => {
            cancelled = true;
            window.cancelAnimationFrame(rafId);
            for (const tid of timeoutIds) {
                window.clearTimeout(tid);
            }
        };
    }, [
        emailFieldDisabledForFocus,
        focusSignUpEmailInput,
        focusSignUpTitleField,
    ]);
    const handleEmailSectionKeyDownCapture = useCallback((event) => {
        if (event.key !== 'Tab' || !event.shiftKey) {
            return;
        }
        if (!backToPricingLinkRef.current) {
            return;
        }
        event.preventDefault();
        backToPricingLinkRef.current.focus();
    }, []);
    const handleBackToPricingKeyDown = useCallback((event) => {
        if (event.key === 'Tab' && !event.shiftKey) {
            event.preventDefault();
            focusSignUpEmailInput();
        }
    }, [focusSignUpEmailInput]);
    useEffect(() => {
        const isNamesValid = values.lastName.length <= VALID_NAME_LENGTH &&
            values.firstName.length <= VALID_NAME_LENGTH;
        const isAllFieldsCompleted = !Object.values(values).includes('');
        const isClinicDomainValid = !hasClinicSignUpPath || !isDomainTaken;
        setIsFormComplete(hasAgreedTermsAndCond &&
            hasConfirmedProviderStatus &&
            isNamesValid &&
            isAllFieldsCompleted &&
            isClinicDomainValid);
    }, [
        values,
        hasAgreedTermsAndCond,
        hasConfirmedProviderStatus,
        isDomainTaken,
        hasClinicSignUpPath,
    ]);
    const handleUpdateNameField = useCallback((event) => {
        const { value, name } = event.target;
        const spChars = /[!"#$%&()*+,./:;<=>?@[\\\]^_{|}]+/;
        // ensure proper length and absence of special chars / unnecessary whitespace (e.g. "Billy Bob" is OK)
        if (spChars.test(value) ||
            value === ' ' ||
            value.slice(-2, value.length) === '  ') {
        }
        else {
            handleChange(event);
        }
    }, [handleChange, setFieldError]);
    const handleUpdatePassword = useCallback((event) => {
        const numPasswordRequirementsMet = getPasswordStrength(event.target.value)
            .contains.length;
        setPasswordStrengthClass(`strength-${numPasswordRequirementsMet}`);
        handleChange(event);
    }, [setPasswordStrengthClass]);
    const handleCloseTitleDropdown = useCallback(() => {
        setFadeOutClass('sign-up__title-option-dropdown_fade-out');
        setTimeout(() => {
            setIsTitleSelectOpen(false);
            setFadeOutClass('');
        }, 200);
    }, [setIsTitleSelectOpen, setFadeOutClass]);
    const handleTitleSelect = useCallback((title) => {
        setFieldValue('title', title, true);
        handleCloseTitleDropdown();
    }, [setFieldValue, setFadeOutClass]);
    const handleToggleTitleSelectOpen = useCallback(() => {
        if (isTitleSelectOpen) {
            handleCloseTitleDropdown();
        }
        else {
            setIsTitleSelectOpen(true);
        }
    }, [isTitleSelectOpen]);
    const handleCheckEmailAvailability = useCallback(async (focusRestoreTarget) => {
        // Uniqueness was already validated before sending a clinic invite.
        if (inviteToken && values.email) {
            return null;
        }
        if (values.email === '' || errors.email) {
            setIsEmailTaken(false);
            setEmailIsAvailable(false);
            return null;
        }
        if (emailAvailabilityCheckInFlightRef.current) {
            return null;
        }
        emailAvailabilityCheckInFlightRef.current = true;
        try {
            const tokenReCaptcha = await getCaptchaToken(captchaRef.current?.getWidgetId(), { focusRestoreTarget });
            if (!tokenReCaptcha) {
                return null;
            }
            setLoadingEmail(true);
            try {
                const { unique } = await checkEmailUnique(values.email, tokenReCaptcha);
                setIsEmailTaken(unique !== true);
                setEmailIsAvailable(unique === true);
            }
            finally {
                setLoadingEmail(false);
            }
        }
        finally {
            // Reset in-flight guard after async work; ref write does not depend on ref read.
            // eslint-disable-next-line require-atomic-updates -- single-writer semaphore for this callback
            emailAvailabilityCheckInFlightRef.current = false;
        }
        return null;
    }, [getCaptchaToken, captchaRef, inviteToken, values.email, errors.email]);
    useAsyncEffect(async () => {
        if (!recaptchaReady || !decodedPrefilledEmail) {
            return;
        }
        const emailInput = document.querySelector('[data-test-id="sign-up-email"] input:not([disabled])');
        await handleCheckEmailAvailability(emailInput ?? null);
    }, [recaptchaReady, decodedPrefilledEmail, handleCheckEmailAvailability]);
    const handleCheckRoomNameAvailability = useCallback(async (roomName) => {
        if (accountType === PlanTypes.Clinic) {
            return true;
        }
        if (values.roomName !== '' && !errors.roomName) {
            setLoadingRoom(true);
            const uniqunessCheck = await checkRoomNameUnique(roomName, currentInstitution.id, inviteToken);
            setIsRoomNameTaken(!uniqunessCheck.isUnique);
            setLoadingRoom(false);
            return uniqunessCheck.isUnique;
        }
        return null;
    }, [
        values.roomName,
        currentInstitution.id,
        errors.roomName,
        inviteToken,
        accountType,
    ]);
    const handleProgressButtonClick = useCallback(() => {
        setHasContinueBeenClicked(true);
        validateForm();
    }, [setHasContinueBeenClicked, validateForm]);
    const handleChangeEmail = useCallback(async (event) => {
        setIsEmailTaken(false);
        handleChange(event);
    }, [handleChange, captchaRef]);
    const handleBlurEmail = useCallback((event) => {
        setFieldTouched('email', true);
        const next = event.relatedTarget;
        // When focus moves into the cross-origin reCAPTCHA iframe, relatedTarget is
        // often null; treat the email field as the restore target so execute() does
        // not strand keyboard/VO focus in the widget.
        const focusRestoreTarget = next instanceof HTMLElement ? next : event.currentTarget;
        void handleCheckEmailAvailability(focusRestoreTarget);
    }, [setFieldTouched, handleCheckEmailAvailability]);
    const debouncedSetRoomName = useDebouncedCallback((value) => {
        setRoomName(value);
    }, 1000);
    const handleChangeRoomName = useCallback((event) => {
        if (event.target.value.length > MAX_SLUG_LENGTH) {
            return;
        }
        setIsRoomNameTaken(false);
        handleChange(event);
        debouncedSetRoomName(event.currentTarget.value);
    }, [setIsRoomNameTaken, handleChange, debouncedSetRoomName]);
    const handleCheckDomainAvailability = useCallback(async () => {
        const { domain } = await institution.findOne({
            filter: { where: { domain: clinicDomain } },
        }).$promise;
        if (Boolean(domain)) {
            setIsDomainTaken(true);
        }
        else {
            setIsDomainTaken(false);
        }
    }, [clinicDomain, institution]);
    const handleChangeCheckinLink = useCallback((event) => {
        setIsDomainTaken(false);
        handleChange(event);
        setClinicDomain(event.currentTarget.value);
    }, [handleChange]);
    const handleChangeClinicName = useCallback((event) => {
        handleChange(event);
    }, [handleChange]);
    const localizedTitles = useLocalizedProviderTitles();
    const providerTitleLocalization = useProviderTitleLocalization();
    const selectedLocalizedTitle = providerTitleLocalization(values.title, true);
    const showEmailValidationError = (hasContinueBeenClicked ||
        decodedPrefilledEmail !== '' ||
        Boolean(touched.email)) &&
        errors.email != null;
    const showTitleValidationError = showSignUpFieldError(hasContinueBeenClicked, touched.title, Boolean(errors.title));
    const showFirstNameValidationError = showSignUpFieldError(hasContinueBeenClicked, touched.firstName, Boolean(errors.firstName));
    const showFirstNameMaxLengthError = values.firstName.length > VALID_NAME_LENGTH;
    const showLastNameValidationError = showSignUpFieldError(hasContinueBeenClicked, touched.lastName, Boolean(errors.lastName));
    const showLastNameMaxLengthError = values.lastName.length > VALID_NAME_LENGTH;
    const showRoomNameValidationError = showSignUpFieldError(hasContinueBeenClicked, touched.roomName, Boolean(errors.roomName));
    const showPasswordValidationError = showSignUpFieldError(hasContinueBeenClicked, touched.password, Boolean(errors.password));
    const showClinicNameValidationError = showSignUpFieldError(hasContinueBeenClicked, touched.clinicName, Boolean(errors.clinicName));
    const showClinicDomainValidationError = showSignUpFieldError(hasContinueBeenClicked, touched.clinicDomain, Boolean(errors.clinicDomain));
    return (<div className="sign-up__container">
      <section className="sign-up__section content-box box-vertical scrollable">
        {!inviteToken && (<a ref={backToPricingLinkRef} className="sign-up__back-button" target="_self" href={`${marketingUrl}/en/pricing/`} tabIndex={-1} onKeyDown={handleBackToPricingKeyDown}>
            <ArrowBack className="sign-up__icon-arrow-btn"/>
            <span className="sign-up__back-button-text">
              <FormattedMessage id="auth.sign-up.goBack"/>
            </span>
          </a>)}
        <header className="logo-header">
          <a className="logo-header-link" href={marketingUrl} rel="noreferer noopener" tabIndex={inviteToken ? undefined : -1} aria-label={formatMessage({ id: 'auth.sign-up.logo.home' })}>
            <img className="logo-header-value" alt="" src={currentInstitution.logoHorizontal ||
            '/assets/images/icons/logo-horizontal.png'}/>
          </a>
        </header>
        <form className="content" name="form" onSubmit={handleSubmit} noValidate>
          <input type="hidden" value={accountType}/>
          {showPaymentSetup && (<SignUpStepper steps={['auth.sign-up.yourInfo', 'auth.sign-up.payment']} currentStepIndex={0}/>)}

          <div className="sign-up__email-input-wrapper ui-form-group" onKeyDownCapture={handleEmailSectionKeyDownCapture}>
            <label className="ui-label" htmlFor="sign-up-email">
              <FormattedMessage id="auth.sign-up.email"/>
            </label>
            <TextInput className={classNames({
            'sign-up__input-field--required': !values.email && (hasContinueBeenClicked || touched.email),
            loading: loadingEmail,
        })} type="email" name="email" id="sign-up-email" data-test-id="sign-up-email" onChange={handleChangeEmail} onBlur={handleBlurEmail} disabled={Boolean(inviteToken && values.email)} value={values.email} {...fieldInvalidProps(showEmailValidationError || isEmailTaken, showEmailValidationError ? SIGN_UP_ERROR_IDS.email : false, isEmailTaken ? SIGN_UP_ERROR_IDS.emailTaken : false)} endAdornment={<Icon className="icon spin5 animate-spin" name="spin5" size="x-small"/>}/>
            <SignUpFieldError id={SIGN_UP_ERROR_IDS.email} show={showEmailValidationError} data-test-id="sign-up-email-invalid">
              {errors.email}
            </SignUpFieldError>
            <SignUpFieldError id={SIGN_UP_ERROR_IDS.emailTaken} show={isEmailTaken}>
              <FormattedMessage id="validation.signUp.unavailableEmail"/>
            </SignUpFieldError>
          </div>
          <Downshift onChange={handleTitleSelect}>
            {({ isOpen: isDownshiftTitleSelectOpen, getToggleButtonProps, getMenuProps, getLabelProps, highlightedIndex, getItemProps, }) => (<div className="ui-form-group">
                <label className="ui-label" {...getLabelProps()}>
                  <FormattedMessage id="auth.sign-up.title"/>
                </label>
                <div onBlur={() => {
                isTitleSelectOpen && handleCloseTitleDropdown();
            }} className="form-group">
                  <button {...getToggleButtonProps()} className={classNames('sign-up__title-select btn button-select', {
                'sign-up__input-field--required': !values.title &&
                    (hasContinueBeenClicked || touched.title),
            })} onClick={handleToggleTitleSelectOpen} onBlur={() => {
                void setFieldTouched('title', true);
            }} type="button" bs-select="bs-select" data-test-id="sign-up-title" {...fieldInvalidProps(showTitleValidationError, showTitleValidationError && SIGN_UP_ERROR_IDS.title)}>
                    {selectedLocalizedTitle}
                    <svg className="sign-up__carret">
                      <use xlinkHref="#arrow-down"/>
                    </svg>
                  </button>

                  <ul {...getMenuProps()} className={`${isTitleSelectOpen || isDownshiftTitleSelectOpen
                ? 'dropdown-menu sign-up__title-option-dropdown--open'
                : 'sign-up__title-option-dropdown--closed'} ${fadeOutClass}`}>
                    {(isTitleSelectOpen || isDownshiftTitleSelectOpen) &&
                localizedTitles.map(({ key: titleKey, label: titleLabel }, index) => (<li style={highlightedIndex === index
                        ? { backgroundColor: '#bde4ff' }
                        : {}} key={titleKey} id="name" className="sign-up__title-option" {...getItemProps({
                    item: titleKey,
                    key: titleKey,
                    index,
                })}>
                            {titleLabel}
                          </li>))}
                  </ul>
                  <SignUpFieldError id={SIGN_UP_ERROR_IDS.title} show={showTitleValidationError} data-test-id="sign-up-title-invalid">
                    {errors.title}
                  </SignUpFieldError>
                </div>
              </div>)}
          </Downshift>
          <div className="ui-form-group">
            <label className="ui-label" htmlFor="firstName">
              <FormattedMessage id="auth.sign-up.firstName"/>
            </label>
            <TextInput className={classNames({
            'form-control--error': showFirstNameValidationError || showFirstNameMaxLengthError,
        }, {
            'sign-up__input-field--required': !values.firstName &&
                (hasContinueBeenClicked || touched.firstName),
        })} id="firstName" name="firstName" data-test-id="sign-up-first-name" onChange={handleUpdateNameField} onBlur={async () => setFieldTouched('firstName', true)} value={values.firstName} {...fieldInvalidProps(showFirstNameValidationError || showFirstNameMaxLengthError, showFirstNameValidationError && SIGN_UP_ERROR_IDS.firstName, showFirstNameMaxLengthError &&
        SIGN_UP_ERROR_IDS.firstNameMaxLength)}/>
            <SignUpFieldError id={SIGN_UP_ERROR_IDS.firstName} show={showFirstNameValidationError} data-test-id="sign-up-first-name-invalid">
              {errors.firstName}
            </SignUpFieldError>
            <SignUpFieldError id={SIGN_UP_ERROR_IDS.firstNameMaxLength} show={showFirstNameMaxLengthError} data-test-id="sign-up-first-name-invalid">
              <FormattedMessage id="form.errors.maxLength" values={{ number: VALID_NAME_LENGTH }}/>
            </SignUpFieldError>
          </div>
          <div className="ui-form-group">
            <label className="ui-label" htmlFor="lastName">
              <FormattedMessage id="auth.sign-up.lastName"/>
            </label>
            <TextInput className={classNames({
            'form-control--error': showLastNameValidationError || showLastNameMaxLengthError,
        }, {
            'sign-up__input-field--required': !values.lastName &&
                (hasContinueBeenClicked || touched.lastName),
        })} id="lastName" name="lastName" data-test-id="sign-up-last-name" value={values.lastName} onChange={handleUpdateNameField} onBlur={async () => setFieldTouched('lastName', true)} {...fieldInvalidProps(showLastNameValidationError || showLastNameMaxLengthError, showLastNameValidationError && SIGN_UP_ERROR_IDS.lastName, showLastNameMaxLengthError &&
        SIGN_UP_ERROR_IDS.lastNameMaxLength)}/>
            <SignUpFieldError id={SIGN_UP_ERROR_IDS.lastName} show={showLastNameValidationError} data-test-id="sign-up-last-name-invalid">
              {errors.lastName}
            </SignUpFieldError>
            <SignUpFieldError id={SIGN_UP_ERROR_IDS.lastNameMaxLength} show={showLastNameMaxLengthError} data-test-id="sign-up-last-name-invalid">
              <FormattedMessage id="form.errors.maxLength" values={{ number: VALID_NAME_LENGTH }}/>
            </SignUpFieldError>
          </div>
          {!hasClinicSignUpPath && (<div className="ui-form-group">
              <label className="ui-label" htmlFor="roomName">
                <FormattedMessage id="auth.room.name"/>
              </label>
              <TextInput className={classNames({
                'sign-up__input-field--required': !values.roomName &&
                    (hasContinueBeenClicked || touched.roomName),
                loading: loadingRoom,
            })} id="roomName" name="roomName" value={values.roomName} onBlur={async (e) => {
                setFieldTouched('roomName', true);
                await handleCheckRoomNameAvailability(e.target.value);
            }} onChange={handleChangeRoomName} disabled={Boolean(inviteToken && isRoomNameDisabled)} data-test-id="sign-up-room-name" formHelperTextProps={{
                as: 'p',
            }} helperText={formatMessage({
                id: 'auth.sign-up.roomName',
            })} {...fieldInvalidProps(showRoomNameValidationError || isRoomNameTaken, showRoomNameValidationError && SIGN_UP_ERROR_IDS.roomName, isRoomNameTaken && SIGN_UP_ERROR_IDS.roomNameTaken)} endAdornment={<Icon className="icon spin5 animate-spin" size="x-small" name="spin5"/>}/>
              <SignUpFieldError id={SIGN_UP_ERROR_IDS.roomName} show={showRoomNameValidationError} data-test-id="sign-up-room-name-invalid">
                {errors.roomName}
              </SignUpFieldError>
              <SignUpFieldError id={SIGN_UP_ERROR_IDS.roomNameTaken} show={isRoomNameTaken}>
                <FormattedMessage id="validation.signUp.unavailableRoomName"/>
              </SignUpFieldError>
              {roomName && (<p className="room-url">{`${origin}/${roomName}`}</p>)}
            </div>)}
          <div className="ui-form-group sign-up__password-wrapper">
            <label className="ui-label" htmlFor="password">
              <FormattedMessage id="auth.sign-up.password"/>
            </label>
            <PasswordField className={classNames({
            'sign-up__input-field--required': !values.password &&
                (hasContinueBeenClicked || touched.password),
        })} formGroupClassName="sign-up__password--margin-top-adjustment" type="password" id="password" name="password" data-test-id="sign-up-password" value={values.password} onChange={handleUpdatePassword} onBlur={async () => setFieldTouched('password', true)} {...fieldInvalidProps(showPasswordValidationError, SIGN_UP_ERROR_IDS.passwordHint, showPasswordValidationError && SIGN_UP_ERROR_IDS.password)}/>
            <p id={SIGN_UP_ERROR_IDS.passwordHint} data-test-id="sign-up-password-tip" className="ui-form-helper-text">
              <FormattedMessage id="auth.sign-up.password-hint"/>
            </p>
            <SignUpFieldError id={SIGN_UP_ERROR_IDS.password} show={showPasswordValidationError} data-test-id="sign-up-password-invalid">
              {errors.password}
            </SignUpFieldError>
            <div className={`strength-meter ${values.password.length > 0 ? passwordStrengthClass : ''}`}>
              <div className="icon icon-circle"/>
              <div className="icon icon-circle"/>
              <div className="icon icon-circle"/>
              <div className="icon icon-circle"/>
              <div className="icon icon-circle"/>
            </div>
          </div>

          {hasClinicSignUpPath && (<>
              <div className="sign-up__separator"></div>
              <div className="ui-form-group">
                <label className="ui-label" htmlFor="sign-up-clinicName">
                  <FormattedMessage id="auth.clinic.name"/>
                </label>
                <TextInput name="clinicName" id="sign-up-clinicName" value={values.clinicName} onChange={handleChangeClinicName} onBlur={async () => setFieldTouched('clinicName', true)} {...fieldInvalidProps(showClinicNameValidationError, showClinicNameValidationError &&
            SIGN_UP_ERROR_IDS.clinicName)} endAdornment={<div className="fix-tooltip-padding">
                      <Tooltip content={<FormattedMessage id="auth.clinic.room.name.tooltip"/>} style={{ padding: '0' }} size="medium" theme="light" hideTimeout={0} debounceMs={0}>
                        <div style={{ stroke: 'currentColor' }}>
                          <Icon name="help" size="x-small" padding={0}/>
                        </div>
                      </Tooltip>
                    </div>}/>
                <SignUpFieldError id={SIGN_UP_ERROR_IDS.clinicName} show={showClinicNameValidationError} data-test-id="sign-up-room-name-invalid">
                  {errors.clinicName}
                </SignUpFieldError>
              </div>
              <div className="ui-form-group sign-up__input-field--link">
                <label className="ui-label check-in-link-label" htmlFor="sign-up-clinic-domain">
                  <FormattedMessage id="auth.clinic.checkin.link"/>
                  <span>{`${clinicDomain.length}/${MAX_CHECK_IN_LINK}`}</span>
                </label>
                <TextInput name="clinicDomain" id="sign-up-clinic-domain" value={values.clinicDomain} onChange={handleChangeCheckinLink} maxLength={MAX_CHECK_IN_LINK} minLength={2} onBlur={() => {
                setFieldTouched('clinicDomain', true);
                void handleCheckDomainAvailability();
            }} {...fieldInvalidProps(showClinicDomainValidationError || isDomainTaken, showClinicDomainValidationError &&
            SIGN_UP_ERROR_IDS.clinicDomain, isDomainTaken && SIGN_UP_ERROR_IDS.clinicDomainTaken)} endAdornment={<div className="end-adornment__domain fix-tooltip-padding">
                      <div>.doxy.me</div>
                      <Tooltip content={<FormattedMessage id="auth.clinic.checkin.link.tooltip"/>} size="medium" theme="light" hideTimeout={0} debounceMs={0}>
                        <span style={{ stroke: 'currentColor' }}>
                          <Icon name="help" size="x-small"/>
                        </span>
                      </Tooltip>
                    </div>} startAdornment={<div>http://</div>}/>
                <SignUpFieldError id={SIGN_UP_ERROR_IDS.clinicDomainTaken} show={isDomainTaken}>
                  <FormattedMessage id="validation.signup.clinicDomain.linkExist"/>
                </SignUpFieldError>
                <SignUpFieldError id={SIGN_UP_ERROR_IDS.clinicDomain} show={showClinicDomainValidationError} data-test-id="sign-up-room-name-invalid">
                  {errors.clinicDomain}
                </SignUpFieldError>
              </div>
              <div className="ui-form-group">
                <label className="ui-label" htmlFor="roomName">
                  <FormattedMessage id="auth.room.personal"/>
                </label>
                <TextInput id="roomName" name="roomName" minLength={2} value={values.roomName} onBlur={async (e) => {
                setFieldTouched('roomName', true);
                await handleCheckRoomNameAvailability(e.target.value);
            }} onChange={handleChangeRoomName} disabled={Boolean(inviteToken && isRoomNameDisabled)} data-test-id="sign-up-room-name" formHelperTextProps={{
                as: 'p',
            }} {...fieldInvalidProps(showRoomNameValidationError || isRoomNameTaken, showRoomNameValidationError && SIGN_UP_ERROR_IDS.roomName, isRoomNameTaken && SIGN_UP_ERROR_IDS.roomNameClinicTaken)} endAdornment={<div className="fix-tooltip-padding">
                      <Tooltip content={<FormattedMessage id="auth.clinic.personal.room.name.tooltip"/>} size="medium" theme="light" hideTimeout={0} debounceMs={0}>
                        <span style={{ stroke: 'currentColor' }}>
                          <Icon name="help" size="x-small"/>
                        </span>
                      </Tooltip>
                    </div>}/>
                <SignUpFieldError id={SIGN_UP_ERROR_IDS.roomName} show={showRoomNameValidationError} data-test-id="sign-up-room-name-invalid">
                  {errors.roomName}
                </SignUpFieldError>
                <SignUpFieldError id={SIGN_UP_ERROR_IDS.roomNameClinicTaken} show={isRoomNameTaken}>
                  <FormattedMessage id="validation.signUp.roomAlreadyExists"/>
                </SignUpFieldError>
                {roomName && clinicDomain && (<p className="room-url personal-room">{`e.g. ${clinicDomain}.doxy.me/${roomName}`}</p>)}
              </div>{' '}
              <div className="sign-up__separator"></div>
            </>)}

          {!isClinicInvite && (<Checkbox id="confirmationHealthCare" checked={hasConfirmedProviderStatus} label={<span>
                  <FormattedMessage id="auth.sign-up.healthCare.text"/>
                  <a href={formatMessage({
                    id: 'auth.sign-up.healthCare.link',
                })} target="_blank" rel="noreferer noopener noreferrer">
                    <FormattedMessage id="auth.sign-up.healthCare.patient"/>
                  </a>
                </span>} onChange={() => setHasConfirmedProviderStatus(!hasConfirmedProviderStatus)}/>)}

          <Checkbox id="confirmationTerms" checked={hasAgreedTermsAndCond} label={<span>
                <FormattedMessage id="auth.sign-up.termsAgree.text"/>
                <a href={formatMessage({
                id: 'auth.sign-up.termsAgree.link',
            })} target="_blank" rel="noreferer noopener noreferrer">
                  <FormattedMessage id="auth.sign-up.termsAgree.termsAndConditions"/>
                  .
                </a>
              </span>} onChange={() => setHasAgreedTermsAndCond(!hasAgreedTermsAndCond)}/>

          <ProgressButton className={classNames('btn btn-primary progress-button space-md-bottom', { 'sign-up__progress-button--disabled': !isFormComplete })} type="submit" validation-submit="form" data-test-id="sign-up-submit" isLoading={isLoading} hasError={Boolean(error)} onClick={handleProgressButtonClick} disabled={!isFormComplete}>
            <span>
              <FormattedMessage id="auth.sign-up.submit"/>
            </span>
          </ProgressButton>
        </form>
      </section>

      <InvisibleReCAPTCHA className="react-recaptcha" ref={captchaRef} sitekey={config.googleReCaptchaV2InvisibleSiteKey} tabIndex={-1} onMounted={() => setRecaptchaReady(true)} onChange={(token) => {
            emitCaptchaEvent('token', token);
        }} onErrored={() => {
            dispatch(coreActions.addToast(ToastType.error, formatMessage({
                id: 'auth.recaptcha.error',
            })));
            emitCaptchaEvent('error');
        }}/>
    </div>);
};
export async function handleSignupSubmit(props) {
    const { handleCheckRoomNameAvailability, inviteToken, hasAgreedTermsAndCond, hasConfirmedProviderStatus, accountType, dispatch, emailIsAvailable, history, values, recaptchaToken, hasClinicSignUpPath, promoCode, } = props;
    // in case the onBlur for calling these isn't triggered
    const roomIsAvailable = await handleCheckRoomNameAvailability(values.roomName);
    const locationService = getAngularService('UserLocationDetails');
    let storedCurrency = window.sessionStorage.getItem(STORAGE_NAME_CURRENCY) ||
        window.localStorage.getItem(STORAGE_NAME_CURRENCY);
    if (!storedCurrency) {
        const locationDetails = await locationService.getLocationDetails();
        const currencyCode = await getUserCurrency(locationDetails);
        if (currencyCode) {
            window.localStorage.setItem(STORAGE_NAME_CURRENCY, currencyCode.toUpperCase());
            window.sessionStorage.setItem(STORAGE_NAME_CURRENCY, currencyCode.toUpperCase());
        }
        storedCurrency = currencyCode || 'USD';
    }
    const signupFormData = {
        ...values,
        accountType,
        email: values.email.trim(),
        title: values.title.trim(),
        roomName: values.roomName.trim(),
        password: values.password.trim(),
        currency: storedCurrency,
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        inviteToken: inviteToken || null,
    };
    if (accountType === PlanTypes.Clinic) {
        if (values.clinicName) {
            signupFormData.clinicName = values.clinicName.trim();
        }
        if (values.clinicDomain) {
            signupFormData.clinicDomain = values.clinicDomain.trim();
        }
    }
    // If the prereqs aren't met, then exit
    if (!hasAgreedTermsAndCond ||
        !hasConfirmedProviderStatus ||
        !emailIsAvailable ||
        !roomIsAvailable) {
        return;
    }
    if (accountType === PlanTypes.Free) {
        // eslint-disable-next-line @typescript-eslint/await-thenable, @typescript-eslint/no-confusing-void-expression
        await dispatch(createAccountAndLogin(signupFormData, recaptchaToken, undefined, {
            routeName: 'account.dashboard',
            routeParams: { afterSignup: true, accountType },
        }, PlanTypes.Free));
        trackCustomEventGA4('sign_up_free', {
            current_subscription: PlanTypes.Free,
        });
        return;
    }
    if (accountType === PlanTypes.Professional) {
        // Not free or clinic = "pro" -- the only time we're taking payment
        trackCustomEventGA4('sign_up_pro_1', {
            current_subscription: PlanTypes.Professional,
        });
        dispatch(storePendingUser(signupFormData));
        dispatch({ type: LOGIN.REQUEST });
        dispatch({ type: LOGIN.SUCCESS });
        setTimeout(() => {
            const queryParams = new URLSearchParams({ accountType });
            if (promoCode) {
                queryParams.append('promo', promoCode);
            }
            history.push(`/payment-setup?${queryParams.toString()}`);
        }, 1000);
    }
    if (accountType === PlanTypes.Clinic) {
        // Clinic signup handling requirements: https://doxyme.atlassian.net/browse/CS-17394
        // eslint-disable-next-line @typescript-eslint/await-thenable, @typescript-eslint/no-confusing-void-expression
        if (hasClinicSignUpPath) {
            // new path for clinic
            trackCustomEventGA4('sign_up_clinic_1', {
                current_subscription: PlanTypes.Clinic,
            });
            dispatch(storePendingUser(signupFormData));
            dispatch({ type: LOGIN.REQUEST });
            dispatch({ type: LOGIN.SUCCESS });
            setTimeout(() => {
                const queryParams = new URLSearchParams({ accountType: 'clinic' });
                if (promoCode) {
                    queryParams.append('promo', promoCode);
                }
                history.push(`/payment-setup?${queryParams.toString()}`);
            }, 1000);
        }
        else {
            trackCustomEventGA4('sign_up_clinic', {
                current_subscription: PlanTypes.Clinic,
            });
            dispatch(createAccountAndLogin(signupFormData, recaptchaToken, undefined, {
                routeName: 'account.subscription',
                routeParams: { modal: 'clinic' },
            }, PlanTypes.Clinic));
        }
    }
}
